/**
 * Calendar constants and helpers.
 */

export const DAY_NAMES = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

export const MONTH_NAMES = [
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

/**
 * Returns day cells for a given year/month.
 * Monday-first grid — null values represent empty leading cells.
 * @param {number} year
 * @param {number} month - 0-based
 * @returns {Array<number|null>}
 */
export function buildDayCells(year, month) {
    const firstDay = new Date(year, month, 1).getDay();
    // Convert Sunday=0 to Monday-first offset
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
}

/**
 * Returns true if the given day matches today's date.
 * @param {number|null} day
 * @param {number} year
 * @param {number} month - 0-based
 * @param {Date} today
 * @returns {boolean}
 */
export function isToday(day, year, month, today) {
    return (
        day !== null &&
        today.getFullYear() === year &&
        today.getMonth() === month &&
        today.getDate() === day
    );
}

/**
 * Returns all events whose ems falls on the given day/month/year, sorted
 * ascending by time. Matches via event.ems (epoch ms) — same source
 * isToday() uses for "today", no dependency on the event.date string format.
 * @param {number|null} day
 * @param {number} year
 * @param {number} month - 0-based
 * @param {CalEventObj[]} events
 * @returns {CalEventObj[]}
 */
export function getEventsForDay(day, year, month, events) {
    if (day === null) return [];

    return events
        .filter((e) => {
            if (!e.ems) return false;
            const d = new Date(e.ems);
            return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
        })
        .sort((a, b) => a.ems - b.ems);
}

/**
 * Returns true if any event in the given list falls on the given day/month/year.
 * @param {number|null} day
 * @param {number} year
 * @param {number} month - 0-based
 * @param {CalEventObj[]} events
 * @returns {boolean}
 */
export function hasEvent(day, year, month, events) {
    return getEventsForDay(day, year, month, events).length > 0;
}

/**
 * Truncates a label to maxChars, appending an ellipsis if it was cut.
 * @param {string} label
 * @param {number} [maxChars] - default 45
 * @returns {string}
 */
export function truncateLabel(label, maxChars = 45) {
    if (!label) return '';
    return label.length > maxChars ? `${label.slice(0, maxChars)}…` : label;
}
